import { AxiosError } from "axios";
import {
  Button,
  Checkbox,
  Container,
  DateInput,
  Form,
  Frequency,
  Input,
  Link,
  List,
  Message,
  RadioGroup,
  Select,
  Stack,
  Text,
} from "basis";
import {
  validateNzMobileNumber,
  validateOver18YearsOld,
} from "library/formValidation";
import { AccountInfo } from "library/hooks/types";
import { yesNoOptions } from "library/sharedOptions";
import { createIncreaseCreditLimitNZTicket } from "library/zendeskAPI";
import { nzEmploymentStatusOptions } from "library/zendeskAPI/customFieldsMap/increaseCreditLimitCustomFieldsMap";
import { Ticket } from "library/zendeskAPI/types";
import { storeRequest } from "library/zendeskAPI/zendeskUtils";
import { legalMap } from "pages/ManageAccount/assetMap";
import { useEventTracking } from "react-event-tracker";
import { useMutation } from "react-query";
import { CreditCard } from "../CreditCard/CreditCard";
import { CreditLimitDetails } from "./components/CreditLimitDetails";
import {
  communicationMethodOptions,
  nzRelationshipStatusOptions,
} from "./options";
import { ActionType, ScreenProps } from "./types";
import { validateNewCreditLimit } from "./validation";

export type NzFormValues = typeof initialValues;

export interface IncreaseCreditLimitNZParam extends NzFormValues {
  userName: string;
  email: string;
  last4digits: string;
  token: string;
  cardType: "gem_visa_card" | "gem_creditline_card" | "gem_essential_card";
}

const cardTypeMap: { [key: string]: IncreaseCreditLimitNZParam["cardType"] } = {
  "Gem Visa": "gem_visa_card",
  "Gem Creditline": "gem_creditline_card",
  "Gem Essential": "gem_essential_card",
};

const initialValues = {
  newCreditLimit: "",
  firstName: "",
  lastName: "",
  dateOfBirth: {
    day: "",
    month: "",
    year: "",
  },
  mobileNumber: "",
  confirm: false,
  maritalStatus: "",
  dependants: "0",
  totalIncome: {
    amount: "",
    frequency: "",
  },
  totalLimit: "",
  expenses: {
    amount: "",
    frequency: "",
  },
  otherExpenses: {
    amount: "",
    frequency: "",
  },
  nzAdditionalIncome: {
    amount: "",
    frequency: "",
  },
  nzSecondaryMortgage: {
    amount: "",
    frequency: "",
  },
  communicationMethod: "",
  nzEmploymentStatus: "",
  nzAnyOtherSourcesOfIncome: "",
  nzIncomeChangesInFuture: "",
  nzAllExpensesForHousehold: "",
  nzHaveOffsetMortgage: "",
  nzPropertyRates: {
    amount: "",
    frequency: "",
  },
  nzRevolvingMortgage: "0",
  nzOtherPropertyExpenses: "",
  nzFoodGroceriesEntertainment: "",
  nzPersonalInsurance: "",
  nzHousingCosts: "",
  nzGeneralInsurance: "",
  nzTransportAuto: "",
  nzEducationChildcare: "",
  nzPersonalFitnessMedical: "",
  nzAnyAdditionalExpense: "",
  nzTotalExpenses: "",
  nzLivingCosts: "",
  nzIncludeLumpSum: "",
  nzIntendLumpSum: "",
  nzTotalOwing: "",
  nzOtherMonthlyLoanRepayments: "",
  nzLoanCommitmentsChanged: "",
  iConfirm: false,
  discloseCredit: false,
  correctInfo: false,
};

export const NzForm = ({ state, dispatch }: ScreenProps) => {
  const { trackEvent } = useEventTracking();
  const accountInfo = state.accountInfo as AccountInfo;
  const token = accountInfo.cardToken;
  const legalCondition = accountInfo.cardName && legalMap[accountInfo.cardName];

  const increaseCreditLimitMutation = useMutation<
    Ticket["id"],
    AxiosError,
    IncreaseCreditLimitNZParam
  >(createIncreaseCreditLimitNZTicket, {
    onSuccess: (id) => {
      trackEvent({
        event: {
          category: "application",
          action: "application-submitted",
          label: "Submit",
          location: "Nz Increase Credit Limit",
        },
      });

      storeRequest({
        userEmail: accountInfo.userEmail,
        accountNumber: accountInfo.accountNumber as string,
        requestType: "increaseCreditLimitNz",
        ticketId: String(id),
      });

      dispatch({ type: ActionType.NEW_CREDIT_LIMIT_SUBMITTED_NZ });
    },
  });

  const onSubmit = ({
    values,
    errors,
  }: {
    values: NzFormValues;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    errors: any;
  }) => {
    if (Object.keys(errors).length === 0) {
      increaseCreditLimitMutation.mutate({
        ...values,
        cardType: cardTypeMap[accountInfo.cardName],
        last4digits: accountInfo.cardEndsWith,
        userName: accountInfo.userName,
        email: accountInfo.userEmail,
        token,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values, errors } }) => {
        const { nzHaveOffsetMortgage } = values;
        return (
          <Stack gap="12">
            <Stack gap="8">
              <Container bg="grey.t05" padding="4">
                <CreditCard
                  cardName={accountInfo.cardName}
                  cardEndsWith={accountInfo.cardEndsWith}
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="newCreditLimit"
                  label="New credit limit"
                  maxLength="5"
                  variant="numeric"
                  prefix="$"
                  validate={validateNewCreditLimit}
                  validateData={{
                    creditLimit: accountInfo.creditLimit,
                    maxCreditLimit: accountInfo.cardMaxCreditLimit,
                  }}
                />
              </Container>
              <CreditLimitDetails
                newCreditLimit={values.newCreditLimit}
                accountInfo={state.accountInfo as AccountInfo}
                errors={errors}
              />

              <Message severity="info-or-minor">
                Your Minimum Monthly Payment is based on a balance up to the
                requested credit limit and the applicable annual percentage rate
                (excluding promotional purchases).
              </Message>

              <Checkbox
                name="iConfirm"
                testId="iConfirm"
                label="Confirm that you can make these payments without financial hardship."
                hideLabel
              >
                I confirm that, based on my current and foreseeable future
                financial circumstances, I will be able to make these payments
                without financial hardship.
                <br />
                Please read{" "}
                <Link href="https://www.verifier.me/privacy" newTab>
                  Privacy Policy
                </Link>{" "}
                and{" "}
                {legalCondition !== "" && (
                  <Link
                    href={`https://assets.latitudefinancial.com/legals/conditions-of-use/${legalCondition}/cou.pdf`}
                    newTab
                  >
                    Conditions of use
                  </Link>
                )}
                .
              </Checkbox>
            </Stack>

            <Stack gap="8">
              <Stack gap="2">
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Personal details
                </Text>
                <Text>
                  Please provide additional information in order to assess your
                  application.
                </Text>
              </Stack>
              <Stack gap="3">
                <Text>
                  <strong>Email address</strong>
                </Text>
                <Text color="grey.t75">{accountInfo.userEmail}</Text>
              </Stack>
              <Stack width="100%" width-sm="400" gap="8">
                <Input name="firstName" label="First name" />
                <Input name="lastName" label="Last name" />

                <DateInput
                  name="dateOfBirth"
                  label="Date of birth"
                  validate={validateOver18YearsOld}
                />
                <Input
                  type="tel"
                  name="mobileNumber"
                  label="Mobile number"
                  validate={validateNzMobileNumber}
                />

                <Select
                  name="maritalStatus"
                  label="Relationship status"
                  options={nzRelationshipStatusOptions}
                />
                <Input
                  name="dependants"
                  label="Number of dependants"
                  maxLength="2"
                  variant="numeric"
                />
              </Stack>
            </Stack>
            <Stack width="100%" width-sm="400" gap="8">
              <Stack gap="2">
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Income
                </Text>
                <Text>Include all taxable income and Government benefits.</Text>
              </Stack>

              <Frequency
                name="totalIncome"
                selectPlaceholder="Frequency"
                mode="select"
                label="Net income (after tax)"
                amountPrefix="$"
                annually={false}
                quarterly={false}
              />
              <Select
                name="nzEmploymentStatus"
                label="Employment Status"
                options={nzEmploymentStatusOptions}
              />

              <RadioGroup
                name="nzAnyOtherSourcesOfIncome"
                label="Do you have any other sources of income?"
                options={yesNoOptions}
              />

              <Frequency
                name="nzAdditionalIncome"
                selectPlaceholder="Frequency"
                mode="select"
                label="Additional income (after tax)"
                amountPrefix="$"
                annually={false}
                quarterly={false}
                optional
              />

              <RadioGroup
                name="nzIncomeChangesInFuture"
                label="Are you aware of any changes in the near future that may reduce
                your income?"
                options={yesNoOptions}
              />
            </Stack>

            <Stack gap="8">
              <Stack gap="2">
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Your monthly expenses
                </Text>

                <Text>
                  If you have included your partner's income then you must
                  include their liabilities in the total expenses.
                </Text>
              </Stack>
              <Stack width="100%" width-sm="400" gap="8">
                <RadioGroup
                  name="nzHousingCosts"
                  label="Have your housing costs changed since your last application?"
                  options={yesNoOptions}
                />
                <RadioGroup
                  name="nzAllExpensesForHousehold"
                  label="Do you pay all the expenses for your household?"
                  options={yesNoOptions}
                />
                <RadioGroup
                  name="nzHaveOffsetMortgage"
                  label="Do you have a revolving or offset mortgage?"
                  options={yesNoOptions}
                />
                {nzHaveOffsetMortgage === "yes" && (
                  <Input
                    name="nzRevolvingMortgage"
                    label="What is your facility limit?"
                    variant="numeric"
                    prefix="$"
                  />
                )}
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Your residential expenses
                </Text>

                <Frequency
                  name="expenses"
                  selectPlaceholder="Frequency"
                  mode="select"
                  label="Mortgage, rent or board"
                  helpText="For your primary residence"
                  amountPrefix="$"
                  annually={false}
                  quarterly={false}
                />
                <Frequency
                  name="nzSecondaryMortgage"
                  selectPlaceholder="Frequency"
                  mode="select"
                  label="Secondary property mortgage, rent or board"
                  helpText="For any secondary or investment property"
                  amountPrefix="$"
                  annually={false}
                  quarterly={false}
                />
                <Frequency
                  name="nzPropertyRates"
                  selectPlaceholder="Frequency"
                  mode="select"
                  label="Property rates"
                  amountPrefix="$"
                  annually={false}
                  quarterly={false}
                  optional
                />
                <Input
                  name="nzOtherPropertyExpenses"
                  label="Utilities and Other Property Expenses"
                  variant="numeric"
                  helpText="e.g. Body corporate fees, water, rubbish collection, recycling, general property maintenance not included in the above"
                  prefix="$"
                />
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Your living expenses
                </Text>
                <Frequency
                  name="otherExpenses"
                  selectPlaceholder="Frequency"
                  mode="select"
                  label="Other loan/hire purchase payments"
                  amountPrefix="$"
                  annually={false}
                  quarterly={false}
                />
                <Input
                  name="nzTotalExpenses"
                  label="Total expenses per month"
                  variant="numeric"
                  prefix="$"
                />

                <RadioGroup
                  name="nzLoanCommitmentsChanged"
                  label="Have your other loan commitments changed since your last application?"
                  options={yesNoOptions}
                />

                <RadioGroup
                  name="nzLivingCosts"
                  label="Have your living costs changed since your last application?"
                  options={yesNoOptions}
                />
                <Input
                  name="nzFoodGroceriesEntertainment"
                  label="Food, groceries and entertainment"
                  variant="numeric"
                  helpText="e.g. Supermarket, bakery, restaurants, takeaway, lunches, coffees"
                  prefix="$"
                />
                <Input
                  name="nzPersonalInsurance"
                  label="Personal Insurance"
                  variant="numeric"
                  prefix="$"
                />
                <Input
                  name="nzGeneralInsurance"
                  label="General Insurance"
                  variant="numeric"
                  helpText="e.g. Home, vehicle, and contents insurance etc."
                  prefix="$"
                />
                <Input
                  name="nzTransportAuto"
                  label="Transport and Auto"
                  variant="numeric"
                  helpText="e.g. Public transport, ride sharing, petrol, parking, vehicle maintenance and registration etc."
                  prefix="$"
                />
                <Input
                  name="nzEducationChildcare"
                  label="Education and Childcare"
                  helpText="e.g. Private school and higher education fees, day-care etc."
                  variant="numeric"
                  prefix="$"
                />
                <Input
                  name="nzPersonalFitnessMedical"
                  label="Personal, Fitness and Medical"
                  variant="numeric"
                  helpText="e.g. Phone, clothing, medical, fitness, vet, doctor etc."
                  prefix="$"
                />
                <Input
                  name="nzAnyAdditionalExpense"
                  label="Any additional expense"
                  helpText="e.g. Child maintenance, donations to family and/or the church etc."
                  variant="numeric"
                  prefix="$"
                />
                <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                  Additional debts
                </Text>
                <RadioGroup
                  name="nzIncludeLumpSum"
                  label="Do any of your current accounts include a lump sum payment either during or at the end of the term?"
                  options={yesNoOptions}
                />
                <RadioGroup
                  name="nzIntendLumpSum"
                  label="Do you intend to use your savings to repay these Lump Sum payments?"
                  options={yesNoOptions}
                />
                <RadioGroup
                  name="nzOtherMonthlyLoanRepayments"
                  label="Do you have any other Other Monthly Loan Repayments?"
                  options={yesNoOptions}
                />
                <Input
                  name="totalLimit"
                  label="Total limit on Credit Cards and/or overdrafts"
                  variant="numeric"
                  prefix="$"
                />

                <Input
                  name="nzTotalOwing"
                  label="Total amount owing on cards and/or overdrafts"
                  variant="numeric"
                  prefix="$"
                />
                <RadioGroup
                  name="communicationMethod"
                  label="Preferred method of communication"
                  options={communicationMethodOptions}
                />
              </Stack>
              <Stack gap="4">
                <Text>I confirm that:</Text>
                <List>
                  <List.Item>
                    <Text textStyle="legal">
                      My credit limit be increased to "Requested Credit Limit",
                      or such lesser credit limit that Latitude Financial
                      Services is willing to provide me.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text textStyle="legal">
                      Electronic income verification will be attempted by our
                      service providers, including Verifier (see Verifier’s
                      terms of service). If your income can’t be verified via
                      these means, you’ll be provided an alternative option.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text textStyle="legal">
                      Latitude Financial Services will complete a credit check
                      during my application to increase my credit limit.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text textStyle="legal">
                      The information I have provided in this application is
                      correct and I have not withheld any information which
                      could cause Latitude Financial Services to incorrectly
                      grant credit to me.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text textStyle="legal">
                      I have read and agree to the Important Privacy Notice.
                    </Text>
                  </List.Item>
                  <List.Item>
                    <Text textStyle="legal">
                      I understand that regardless of the outcome of my
                      application, or if I choose to cancel after submitting, I
                      will need to wait at least 30 days before applying again.
                    </Text>
                  </List.Item>
                </List>
              </Stack>
              <Checkbox
                name="discloseCredit"
                testId="discloseCredit"
                label="Confirm that the Credit Provider may disclose information to a credit reporting body."
                hideLabel
              >
                I have read and agree to all of the above.
              </Checkbox>
              <Button
                type="submit"
                width="200"
                loading={increaseCreditLimitMutation.isLoading}
              >
                Submit
              </Button>
            </Stack>
          </Stack>
        );
      }}
    </Form>
  );
};
